import {datadogRum, type RumGlobal} from '@datadog/browser-rum';
import {once} from 'lodash';
import {useEffect} from 'react';

import {envIsPublicCloud} from '../../config';
import {ignoredErrorMessages} from '../ignoredErrorMessages';
import {
  hasViewEverBeenBackgrounded,
  resetViewBackgrounded,
} from '../viewVisibility';
import {
  ALLOWED_TRACING_URLS,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';
import {useDatadogContext} from './DatadogContext';
import {
  TTI_DOMAIN_INCLUSIONS,
  TTI_WANDB_ANALYTICS_DOMAIN,
} from './profiler/constants';
import {redactRumContext} from './redactRumContext';
import {
  redactGraphQlSensitiveFields,
  redactSensitiveUrlParams,
} from './redactSensitiveFields';

const rumFacade = Object.create(datadogRum);

rumFacade.addDurationVital = skipFnIfHidden(datadogRum.addDurationVital);
rumFacade.startDurationVital = skipFnIfHidden(datadogRum.startDurationVital);
rumFacade.stopDurationVital = skipFnIfHidden(datadogRum.stopDurationVital);
rumFacade.addTiming = skipFnIfHidden(datadogRum.addTiming);
rumFacade.addAction = skipFnIfHidden(datadogRum.addAction);

/**
 * Returns a facade over the Datadog RUM global that skips certain tracking calls when
 * the window has been backgrounded, avoiding metrics that are inaccurate while the page
 * is hidden.
 *
 * @returns The Datadog RUM facade with view visibility checks
 */
export function getDatadogRum(): RumGlobal {
  return rumFacade;
}

/**
 * React hook to access the Datadog RUM (Real User Monitoring) instance.
 *
 * @returns The Datadog RUM global instance
 */
export function useDatadogRum(): RumGlobal {
  return useDatadogContext().rum;
}

/**
 * Which RUM mechanism to reach for (view/global context are documented on their
 * wrapper functions below):
 * - Actions (`getDatadogRum().addAction`): discrete, user-initiated interactions
 *   (clicks, submits) — not timing data or passive/derived metadata. Names governed by
 *   the action-name allowlist.
 * - Duration vitals (`getDatadogRum().addDurationVital`): how long an operation took
 *   (e.g. workspace save). Vitals inherit the current view's context, so prefer these
 *   over stuffing durations into an action payload. `startTime` is an absolute epoch
 *   timestamp (`performance.timeOrigin + <relative perf.now() mark>`).
 */

/**
 * Sets a view-context property: a stable, low-cardinality dimension scoped to the
 * current view, stamped on every event for that view and reset when a new view starts.
 * Prefer this (and the `use*RumViewContext` hooks) over `datadogRum.setViewContextProperty`
 * directly; `key` must be allowlisted in `datadogContextKeys.json` (enforced by
 * `wandb/dd-context-property-allowlist`), keeping dashboard keys stable.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumViewContextProperty(key: string, value: unknown): void {
  getDatadogRum().setViewContextProperty(key, value);
}

/**
 * Sets a global/session context property: a session-wide attribute spanning all views,
 * stamped on every event regardless of route. Prefer view context when the value can
 * change as the user navigates; same `datadogContextKeys.json` allowlist applies.
 *
 * @param key - allowlisted context property key
 * @param value - context value
 */
export function setRumGlobalContextProperty(key: string, value: unknown): void {
  getDatadogRum().setGlobalContextProperty(key, value);
}

/**
 * Removes a session/global context key. No view-context equivalent exists — view
 * context is cleared automatically on view change.
 *
 * @param key - allowlisted context property key to remove
 */
export function removeRumGlobalContextProperty(key: string): void {
  getDatadogRum().removeGlobalContextProperty(key);
}

/**
 * Sets a global/session context property (session-wide, spans all views) for the
 * hook's lifetime; unset when the property changes or the component unmounts. Prefer
 * view context when the value can change as the user navigates.
 *
 * @param key
 * @param value
 */
export function useRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
      return () => {
        removeRumGlobalContextProperty(key);
      };
    }
    return undefined;
  }, [key, value, shouldLog]);
}

/**
 * As with `useRumGlobalContext` (session-wide attribute spanning all views; prefer
 * view context for values that change on navigation), but does not unset on unmount.
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumGlobalContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

/**
 * Sets a view-context property (stable, low-cardinality dimension scoped to the current
 * view; allowlisted in `datadogContextKeys.json`), retained until a new view starts.
 *
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumViewContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  useEffect(() => {
    if (shouldLog) {
      setRumViewContextProperty(key, value);
    }
  }, [key, value, shouldLog]);
}

const datadogRumViewTimings = new Set<string>();

/**
 * Returns the set of timing names that have been recorded in the current view.
 * This is used to track which timings have already been logged.
 *
 * Lives here (not in `datadogRumTimings.ts`) because it is per-view state owned
 * by the view lifecycle: `startDatadogRumView`/`clearDatadogRumView` reset it,
 * and keeping it in the timings module would create an import cycle with the
 * timing hooks that call `getDatadogRum()`.
 *
 * @returns The set of recorded timing names
 */
export function getDatadogRumViewTimings() {
  return datadogRumViewTimings;
}

/**
 * Starts a new Datadog RUM view with the given name.
 * Clears view-specific timings and resets the view backgrounded state.
 *
 * @param name - The name of the view/route being started
 */
export function startDatadogRumView(name?: string) {
  // See `addFirstTiming` -- clear out the list of already-recorded timings so
  // that they can be re-recorded for the new view.
  getDatadogRumViewTimings().clear();

  const datadogRum = getDatadogRum();
  resetViewBackgrounded();
  // `startView({name})` sets `@view.name`; filter/group RUM by that instead of a
  // separate (session-global) `routeName` context property.
  datadogRum.startView({name});
}

/**
 * Clear out Datadog RUM timings and view backgrounded state
 */
export function clearDatadogRumView() {
  getDatadogRumViewTimings().clear();
  resetViewBackgrounded();
}

/**
 * Initializes Datadog RUM (Real User Monitoring) with configuration for session replay,
 * user tracking, resource monitoring, and custom event filtering.
 * This function is wrapped with `once()` to ensure it only runs once.
 */
export const initDatadogRum = once(function initDatadogRum() {
  datadogRum.init({
    // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_resource_performance/#track-graphql-requests
    allowedGraphQlUrls: [
      {
        match: /\/graphql$/,
        trackPayload: true, // Include GraphQL query (limited to 32 KB)
      },
    ],
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    // v7 defaults propagateTraceBaggage to true, which sends a `baggage` header
    // on traced cross-origin requests. Keep it off until backend CORS allows the
    // header, to avoid breaking traced API calls.
    propagateTraceBaggage: false,

    applicationId: DATADOG_RUM_APPLICATION_ID,
    beforeSend(event) {
      event.view.url = redactSensitiveUrlParams(event.view.url);
      if (event.type === 'action' && event.action?.target) {
        const targetUrl = event.action.target.url;
        if (typeof targetUrl === 'string') {
          event.action.target.url = redactSensitiveUrlParams(targetUrl);
        }
      }
      if (event.type === 'resource' && event.resource?.url) {
        event.resource.url = redactSensitiveUrlParams(event.resource.url);
      }

      // https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
      const discardedErrorMessages = [
        "'measure' on 'Performance': The mark 'GTM", // Google Tag manager
        'Performance.measure: Given mark name', // Google Tag manager
        'https://jscloud.net/x/25796/inlinks.js',
        ...ignoredErrorMessages,
      ];

      if (event.type === 'error') {
        if (
          discardedErrorMessages.some(msg => event.error.message.includes(msg))
        ) {
          return false;
        }
      }
      if (event.type === 'resource') {
        redactGraphQlSensitiveFields(event);
      }

      const context = event.context ?? {};
      event.context = context;

      context.gitTag = window.CONFIG?.GIT_TAG;
      context.envIsPublicCloud = envIsPublicCloud;

      // Single sanitization point for every event type: per-call context (actions,
      // vitals, errors) and view/global context all merge into event.context by the
      // time beforeSend runs.
      event.context = redactRumContext(context);

      return true;
    },
    clientToken: DATADOG_CLIENT_TOKEN,
    // SECURITY: Mask all text in session replays to prevent sensitive data exposure.
    // If you want to see an element in session replay, use data-dd-privacy="allow"
    // on specific elements to selectively unmask non-sensitive content.
    defaultPrivacyLevel: 'mask',
    env: window.CONFIG?.ENVIRONMENT_NAME,
    profilingSampleRate: 25,
    service: DATADOG_UI_SERVICE,
    version: window.CONFIG?.GIT_TAG,
    sessionReplaySampleRate: 100,
    site: DATADOG_SITE,
    traceSampleRate: 100,
    // propagate feature flag list to additional even types
    trackFeatureFlagsForEvents: ['vital', 'action', 'long_task', 'resource'],
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,
    // Only include activity from domains that are in TTI_DOMAIN_INCLUSIONS
    excludedActivityUrls: [
      (url: string) => {
        const urlobj = new URL(url);
        return !TTI_DOMAIN_INCLUSIONS.some(
          domain =>
            urlobj.hostname.includes(domain) &&
            // exclude analytics.wandb.ai - this is a special case because it includes wandb.ai, which is allowed
            urlobj.hostname !== TTI_WANDB_ANALYTICS_DOMAIN
        );
      },
    ],
    trackViewsManually: true,
  });
});

/** Internal functions - please think twice before exporting --------------------------------- */

/**
 * Wraps a Datadog analytics function and returns a new function with the same
 * signature. If the current browser window has ever been backgrounded during
 * the current view, the Datadog function will not be called. This prevents
 * inaccurate metrics from being recorded when the page is not visible.
 * In either case the return value should be the same-ish.
 *
 * @param fn - The Datadog function to wrap
 * @param generateReturnOnHidden - Optional function to generate a return value when the view is hidden
 * @returns A wrapped function that checks view visibility before calling the original
 */
function skipFnIfHidden<Fn extends (...args: any) => any>(
  fn: Fn,
  generateReturnOnHidden?: () => ReturnType<Fn>
) {
  return function (this: any, ...args: Parameters<Fn>): ReturnType<Fn> {
    if (hasViewEverBeenBackgrounded()) {
      if (generateReturnOnHidden) {
        return generateReturnOnHidden();
      }
      return undefined as ReturnType<Fn>;
    }
    return fn.call(this, ...args);
  };
}
